/**
 * Feature flags — a single place to turn functionality on/off per environment.
 *
 * These are **build-time** flags: their values come from `FEATURE_*` env vars, baked
 * into the bundle by webpack (`DefinePlugin`, see `config/webpack.common.cjs`), so
 * flipping one means changing the env var and rebuilding/redeploying. A flag whose env
 * var is **unset (or empty) defaults to OFF** — a feature must be explicitly enabled.
 *
 * Usage in code: read `featureFlags.<name>` (a boolean). See
 * `docs/features/feature-flags.md` for the how-to and the on/off quick reference.
 *
 * Runtime (no-redeploy) toggling — e.g. GitLab Feature Flags / Unleash — is a drop-in
 * later: keep the `featureFlags.*` call sites and swap the `envEnabled(...)` source for
 * an Unleash `isEnabled('...')` lookup.
 */

const TRUTHY = new Set(["true", "1", "on", "yes"]);
const FALSY = new Set(["false", "0", "off", "no"]);

/** Parse a flag env var. Unset/empty/unrecognised → `defaultOn` (which is `false` for our flags). */
export function envEnabled(raw: string | undefined, defaultOn = false): boolean {
  if (raw == null || raw.trim() === "") return defaultOn;
  const v = raw.trim().toLowerCase();
  if (TRUTHY.has(v)) return true;
  if (FALSY.has(v)) return false;
  return defaultOn;
}

export const featureFlags = {
  alignv: envEnabled(process.env.FEATURE_ALIGNV),
  /**
   * In-browser AI photo classifier on the add-patient page. OFF → that step renders
   * only the legacy 8-slot upload grid (and the model / onnxruntime chunk never loads).
   * Enable with `FEATURE_PHOTO_CLASSIFIER=true`. Default: OFF.
   */
  photoClassifier: envEnabled(process.env.FEATURE_PHOTO_CLASSIFIER),

  /**
   * redesign-v1: the shadcn/Tailwind visual redesign (dark neutral theme, Obra token set).
   * OFF → the app renders its current (legacy Bootstrap) UI everywhere and no redesign CSS,
   * tokens, or `[data-redesign-v1]` root ships. ON → redesign routes/pages become reachable.
   * The redesign lives in `app/common/redesign-v1/` and is loaded lazily behind this flag so
   * nothing downloads when it's off. Enable with `FEATURE_REDESIGN_V1=true`. Default: OFF.
   */
  redesignV1: envEnabled(process.env.FEATURE_REDESIGN_V1),

  /**
   * redesign-v1 ROUTE OVERRIDE: serve redesigned pages under their **legacy route** instead of
   * only at the `/pages/redesign/*` route. A second, redesign-scoped flag layered on top of
   * `redesignV1` (it only has any effect when `redesignV1` is also ON — see `app/index.tsx`
   * `REDESIGN_ROUTE_OVERRIDES`). OFF → the redesigned page stays reachable only at its
   * `/pages/redesign/…` route and the legacy page keeps its route. ON → the legacy route renders
   * the redesigned page (e.g. `/pages/meetings` → the redesigned expert-consultation listing),
   * so the redesign can be dogfooded in place without moving the URL. Enable with
   * `FEATURE_REDESIGN_V1_ROUTE_OVERRIDE=true`. Default: OFF.
   */
  redesignV1RouteOverride: envEnabled(process.env.FEATURE_REDESIGN_V1_ROUTE_OVERRIDE),

  /**
   * Экспертное сопровождение: запись/загрузка аудио → транскрипт
   * (SpeechKit) → обработка моделью (grok) → авто-заполнение формы рецепта.
   * OFF → доп-услуга «Экспертное сопровождение» не предлагается и весь аудио-flow
   * не рендерится (обычный рецепт). Enable with `FEATURE_SMART_PRESCRIPTION=true`.
   * Default: OFF.
   */
  smartPrescription: envEnabled(process.env.FEATURE_SMART_PRESCRIPTION),

  /**
   * Платный блок «Дополнительные услуги» на форме заявки (add/edit patient).
   * Выбранные услуги уезжают в payload как `additional_service_tags`.
   *
   * OFF → блок рендерится ровно так, как в релизе 36.2.12 (`224f6a2b`): заголовок
   * «Дополнительные инструкции» + один чекбокс «Глубокий анализ КЛКТ», и ключ
   * `additional_service_tags` НИКОГДА не попадает в тело запроса.
   *
   * Включать флаг можно только в средах, где backend принимает `additional_service_tags`.
   *
   * Enable with `FEATURE_ADDITIONAL_PAID_SERVICES=true`. Default: OFF.
   */
  additionalPaidServices: envEnabled(process.env.FEATURE_ADDITIONAL_PAID_SERVICES),

  // Controls push subscription settings; service worker registration is independent of this flag.
  pushNotifications: envEnabled(process.env.FEATURE_PUSH_NOTIFICATIONS),

  maintenanceMode: envEnabled(process.env.FEATURE_MAINTENANCE_MODE),

  pwaOnboarding: envEnabled(process.env.FEATURE_PWA_ONBOARDING),
};
